import { Box, Link, Paper, Stack, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { XpmLogo } from '../XpmLogo';
import NewBadge from './assets/new-badge.svg';

type XpmIntegrationCtaProps = {
  onLearnMore?: () => void;
  learnMoreUrl?: string;
  showNewBadge?: boolean;
};

/**
 * Hardcoded values from Figma that don't have corresponding design tokens:
 * - Logo container size: 30x30px (Figma: size-[30px])
 * - Logo container padding: 3px (Figma: p-[3px])
 * - Container border radius: 4px (no matching borderRadius token)
 * - Icon size: 24px (ArrowForward icon)
 * - NEW badge: SVG image 58x20px (Figma), exported as 68x30px to include Dropshadow/2 effect
 *   Color: Secondary/P500 (#5F48FB) with Primary/White outline
 */

const XpmIntegrationCtaComponent = ({
  onLearnMore,
  learnMoreUrl,
  showNewBadge = true,
}: Readonly<XpmIntegrationCtaProps>) => {
  const handleLearnMore = () => {
    if (onLearnMore) {
      onLearnMore();
    } else if (learnMoreUrl) {
      window.open(learnMoreUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Paper
      elevation={1}
      sx={(theme) => ({
        p: theme.semantic.spacing.base,
        borderRadius: '4px', // Hardcoded: Figma rounded-[4px]
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      })}
    >
      {/* Left content */}
      <Stack
        direction="row"
        alignItems="center"
        sx={(theme) => ({
          gap: theme.semantic.spacing.base,
        })}
      >
        {/* XPM Logo - 30x30px */}
        <XpmLogo size="small" />

        {/* Title */}
        <Typography variant="h5">Integrate with Xero Practice Manager</Typography>

        {/* NEW Badge - Figma: 58x20px container with Dropshadow/2 effect */}
        {showNewBadge && (
          <Box
            sx={{
              width: '58px',
              height: '20px',
              flexShrink: 0,
              overflow: 'visible',
            }}
          >
            <NewBadge aria-label="New" />
          </Box>
        )}
      </Stack>

      {/* Learn More Link */}
      <Link
        component="button"
        onClick={handleLearnMore}
        underline="always"
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: theme.semantic.spacing.xs,
          color: theme.palette.primary.main,
          fontSize: theme.semantic.fontSize.base,
          fontWeight: theme.semantic.fontWeight.regular,
          cursor: 'pointer',
        })}
      >
        Learn more
        {/* Hardcoded: 24px icon size */}
        <ArrowForwardIcon sx={{ fontSize: 24 }} />
      </Link>
    </Paper>
  );
};

export const XpmIntegrationCta = withClarityTheme(XpmIntegrationCtaComponent);

export type { XpmIntegrationCtaProps };
